import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import cn from 'classnames';
import FeatherIcon from 'feather-icons-react';
import { useLocale } from 'hooks/locale';
import moment from 'moment';
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';

import {
  Checkbox,
  DatePicker,
  DateRangePicker,
  InputGroup,
  MultipleSelect,
  SearchInput,
  Select,
} from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';
import { IconButton } from 'components/IconButton/IconButton';

import styles from './Table.module.scss';

const TableContext = createContext(null);

const useTableContext = () => {
  const context = useContext(TableContext);
  if (!context)
    throw new Error(`Table compound components cannot be rendered outside the Table component`);

  return context;
};

/**
 * Optional rules to normalize multi-select filters so "All" / exclusive options work both ways.
 * Keys are filter names; values: { allValue, allName?, exclusiveValue?, exclusiveName? }.
 * - User just selected "All" (all not in previous value) → keep only [allValue].
 * - User just selected "exclusive" (e.g. Support Checks) → keep only [exclusiveValue].
 * - User had "All" or "exclusive" and then ticked another option → remove "all"/"exclusive", keep the rest.
 */
const normalizeMultiSelectParams = (merged, multiSelectNormalize, prevParams) => {
  if (!multiSelectNormalize || typeof multiSelectNormalize !== 'object') return merged;
  const next = { ...merged };
  for (const [key, rule] of Object.entries(multiSelectNormalize)) {
    const param = next[key];
    const val = param?.value;
    if (!param || !Array.isArray(val)) continue;
    const prevVal = Array.isArray(prevParams?.[key]?.value) ? prevParams[key].value : [];
    const hasAll = rule.allValue && val.includes(rule.allValue);
    const hadAll = rule.allValue && prevVal.includes(rule.allValue);
    const hasExclusive = rule.exclusiveValue && val.includes(rule.exclusiveValue);
    const hadExclusive = rule.exclusiveValue && prevVal.includes(rule.exclusiveValue);

    const filteredVal = val.filter(v => v !== rule.allValue && v !== rule.exclusiveValue);
    const nameArr = param.name && Array.isArray(param.name) ? param.name : [];
    const filteredName =
      nameArr.length === val.length
        ? nameArr.filter((_, i) => val[i] !== rule.allValue && val[i] !== rule.exclusiveValue)
        : filteredVal.map(() => undefined);
    const filteredSet = { ...param, value: filteredVal, name: filteredName };

    if (hasAll && !hadAll) {
      next[key] = {
        ...param,
        value: [rule.allValue],
        name: [rule.allName ?? rule.allValue],
      };
    } else if (hasExclusive && !hadExclusive) {
      next[key] = {
        ...param,
        value: [rule.exclusiveValue],
        name: [rule.exclusiveName ?? rule.exclusiveValue],
      };
    } else if (
      (hasAll && hadAll && val.length > 1) ||
      (hasExclusive && hadExclusive && val.length > 1)
    ) {
      next[key] = filteredSet;
    } else if (hasAll && val.length === 1) {
      next[key] = {
        ...param,
        value: [rule.allValue],
        name: [rule.allName ?? rule.allValue],
      };
    } else if (hasExclusive && val.length === 1) {
      next[key] = {
        ...param,
        value: [rule.exclusiveValue],
        name: [rule.exclusiveName ?? rule.exclusiveValue],
      };
    } else {
      next[key] = filteredSet;
    }
  }
  return next;
};

const Context = ({
  children,
  onQueryUpdate = () => {},
  resetToDefaultQuery = false,
  defaultQuery = {},
  manualQueryUpdate = false,
  onSelectedIdsChange = () => {},
  onBulkUseAllChange = () => {},
  onClearQueryParams = () => {},
  multiSelectNormalize = null,
}) => {
  const [queryParams, setQueryParams] = useState(defaultQuery);
  const [sorting, setSorting] = useState([]);

  const [selectedAll, setSelectedAll] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkUseAll, setBulkUseAll] = useState(false);

  const [initialQueryApplied, setInitialQueryApplied] = useState(false);

  const [prevPageRef, setPrevPageRef] = useState(1);

  useEffect(() => {
    onSelectedIdsChange(selectedIds);
  }, [selectedIds]);

  useEffect(() => {
    onBulkUseAllChange(bulkUseAll);
  }, [bulkUseAll]);

  useEffect(() => {
    const currentPage = queryParams?.page?.value;
    const pageChanged = currentPage !== prevPageRef;
    const sortingChanged = sorting.length > 0;

    const shouldSkip = manualQueryUpdate && initialQueryApplied && !pageChanged && !sortingChanged;

    if (shouldSkip) return;

    applyFilters();

    setSelectedAll(false);
    setSelectedIds([]);

    setInitialQueryApplied(true);
    setPrevPageRef(currentPage);
  }, [queryParams, sorting]);

  useEffect(() => {
    if (!selectedAll) setSelectedIds([]);
  }, [selectedAll]);

  const updateQueryParams = params =>
    setQueryParams(curr => {
      const merged = { ...curr, ...params };
      return normalizeMultiSelectParams(merged, multiSelectNormalize, curr);
    });

  const clearQueryParams = () => {
    const { page } = queryParams;
    setQueryParams({ page });
    if (resetToDefaultQuery) {
      setQueryParams(defaultQuery);
      onClearQueryParams();
    }
  };

  const applyFilters = () => {
    const params = Object.keys(queryParams).reduce((acc, key) => {
      const value = queryParams[key]?.value;
      if (Array.isArray(value) ? value.length > 0 : !!value) {
        acc[key] = value;
      }
      return acc;
    }, {});
    if (sorting?.length > 0) {
      params['sorting'] = sorting.join(',');
    }
    // Pass both flat params (for API) and full queryParams (for storage with names)
    onQueryUpdate(params, queryParams);
  };

  const manuallyApplyFilters = () => {
    applyFilters();
  };

  const contextValue = {
    queryParams,
    updateQueryParams,
    clearQueryParams,
    selectedAll,
    setSelectedAll,
    selectedIds,
    setSelectedIds,
    bulkUseAll,
    setBulkUseAll,
    sorting,
    setSorting,
    manuallyApplyFilters,
  };

  return <TableContext.Provider value={contextValue}>{children}</TableContext.Provider>;
};

const TableContent = ({ className, children }) => (
  <div className={cn(styles.root, { [className]: className })}>{children}</div>
);

export const Table = ({ children }) => <table className={styles.table}>{children}</table>;

const Placeholder = ({ children }) => <p className={styles.placeholder}>{children}</p>;

const Head = ({ children }) => {
  return <thead className={styles.head}>{children}</thead>;
};

const Header = ({
  children,
  align = 'start',
  bulkSelector = false,
  sortKey = false,
  noBorder = false,
}) => {
  const { selectedAll, setSelectedAll, setSorting, updateQueryParams } = useTableContext();

  const [sortAsc, setSortAsc] = useState(true);

  const sort = () => {
    if (!sortKey) return;

    const newSortAsc = !sortAsc;
    setSortAsc(newSortAsc);

    setSorting(curr => {
      curr = curr.filter(item => item.split(':')[0] !== sortKey);
      return [`${sortKey}:${newSortAsc ? 'asc' : 'desc'}`, ...curr];
    });
    updateQueryParams({ page: { value: 1 } });
  };

  return (
    <th className={cn(styles.header, { [styles.headerNoBorder]: noBorder })}>
      {bulkSelector ? (
        <Checkbox
          checked={selectedAll}
          onClick={() => setSelectedAll(curr => !curr)}
          label={<span style={{ fontSize: '14px' }}>{children}</span>}
        />
      ) : (
        <span
          className={cn(
            'd-flex',
            'align-items-center',
            { ['u-pointer']: !!sortKey },
            `u-justify-self-${align}`,
          )}
          onClick={sort}>
          {children}
          {sortKey && (
            <FeatherIcon
              className="u-margin-left--small"
              icon={sortAsc ? 'chevron-down' : 'chevron-up'}
              size={15}
            />
          )}
        </span>
      )}
    </th>
  );
};

const Body = ({ children }) => <tbody className={styles.body}>{children}</tbody>;

const Row = ({ children }) => <tr className={styles.row}>{children}</tr>;

const Col = ({ children, align = 'left', bulkSelector, selectorId, className }) => {
  const [checked, setChecked] = useState(false);

  const { selectedAll, selectedIds, setSelectedIds } = useTableContext();

  useEffect(() => {
    if (!bulkSelector) return;

    if (selectedAll)
      setSelectedIds(curr => (curr.includes(selectorId) ? curr : [...curr, selectorId]));
  }, [selectedAll]);

  useEffect(() => {
    if (!bulkSelector) return;

    setChecked(selectedIds.includes(selectorId));
  }, [selectedIds]);

  const select = value => {
    if (value) setSelectedIds(curr => (curr.includes(selectorId) ? curr : [...curr, selectorId]));
    else setSelectedIds(curr => curr.filter(val => val !== selectorId));
  };

  return (
    <td className={cn(styles.column, { [className]: className })} style={{ textAlign: align }}>
      {bulkSelector ? (
        <Checkbox
          checked={checked}
          onClick={select}
          label={<span style={{ fontSize: '14px', fontWeight: '600' }}>{children}</span>}
        />
      ) : (
        children
      )}
    </td>
  );
};

const PAGE_SIZE_OPTIONS = [10, 15, 20, 25, 30];

const Pagination = ({ page, records_per_page, total_records, total_pages, onPageSizeChange }) => {
  const { queryParams, updateQueryParams } = useTableContext();

  const pages = useMemo(() => {
    if (total_pages < 10) {
      return Array.from({ length: total_pages }, (_, i) => i + 1);
    } else if (page <= 5) {
      return Array.from({ length: 10 }, (_, i) => i + 1);
    } else if (page + 6 > total_pages) {
      return Array.from({ length: 10 }, (_, i) => i + (total_pages - 9));
    } else {
      return Array.from({ length: 10 }, (_, i) => i + (page - 5));
    }
  }, [page, total_pages]);

  const firstRecordInPage = useMemo(() => {
    return (page - 1) * records_per_page + 1;
  }, [page, records_per_page]);

  const lastRecordInPage = useMemo(() => {
    const lastRecord = page * records_per_page;
    return lastRecord > total_records ? total_records : lastRecord;
  }, [page, records_per_page, total_records]);

  const setPage = newPage => {
    if (newPage === queryParams.page?.value) return;
    updateQueryParams({ page: { value: newPage } });
  };

  const firstPage = () => {
    if (page === 1) return;
    setPage(1);
  };

  const previousPage = () => {
    if (page === 1) return;
    setPage(page - 1);
  };

  const nextPage = () => {
    if (page === total_pages) return;
    setPage(page + 1);
  };

  const lastPage = () => {
    if (page === total_pages) return;
    setPage(total_pages);
  };

  const selectPage = pageToGo => {
    if (page === pageToGo) return;
    setPage(pageToGo);
  };

  const handlePageSizeChange = e => {
    const newSize = parseInt(e.target.value, 10);
    if (onPageSizeChange) {
      updateQueryParams({ page: { value: 1 } });
      onPageSizeChange(newSize);
    }
  };

  return (
    <tfoot className={styles.footer}>
      <tr className={styles.pagination}>
        <td colSpan="4" className={styles.paginationDescription}>
          {onPageSizeChange && (
            <span className={styles.pageSizeSelector}>
              <span className={styles.pageSizeLabel}>Items per page:</span>
              <select
                className={styles.pageSizeSelect}
                value={records_per_page}
                onChange={handlePageSizeChange}>
                {PAGE_SIZE_OPTIONS.map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </span>
          )}
          Showing:{' '}
          <span className="font-semibold">
            {firstRecordInPage} - {lastRecordInPage} of {total_records} results
          </span>
        </td>
        <td className={styles.paginationLinks} colSpan="100%">
          <div className="flex items-center justify-end gap-1">
            <FeatherIcon
              className={cn(styles.chevron, { [styles.chevronDisabled]: page === 1 })}
              icon="chevrons-left"
              size={18}
              onClick={firstPage}
            />
            <FeatherIcon
              className={cn(styles.chevron, 'mr-2', {
                [styles.chevronDisabled]: page === 1,
              })}
              icon="chevron-left"
              size={18}
              onClick={previousPage}
            />
            {pages.map(index => (
              <span
                key={index}
                onClick={() => selectPage(index)}
                className={cn(styles.paginationNumber, {
                  [styles.paginationNumberSelected]: page === index,
                })}>
                {index}
              </span>
            ))}
            <FeatherIcon
              className={cn(styles.chevron, 'ml-2', {
                [styles.chevronDisabled]: page === total_pages,
              })}
              icon="chevron-right"
              size={18}
              onClick={nextPage}
            />
            <FeatherIcon
              className={cn(styles.chevron, { [styles.chevronDisabled]: page === total_pages })}
              icon="chevrons-right"
              size={18}
              onClick={lastPage}
            />
          </div>
        </td>
      </tr>
    </tfoot>
  );
};

const ActionButtons = ({ children }) => {
  return <div className={styles.actionButtons}>{children}</div>;
};

const FloatingFilters = ({ children, className }) => {
  const { queryParams, updateQueryParams, clearQueryParams } = useTableContext();

  const filters = useMemo(() => {
    const filterValues = [];
    const filterKeys = Object.keys(queryParams).filter(key => key !== 'page' && key !== 'search');

    filterKeys.forEach(key => {
      switch (queryParams[key]?.type) {
        case 'date_range':
          filterValues.push({
            title: queryParams[key].title,
            name:
              moment(queryParams[key].value[0]).format('MMM D, YYYY') +
              ' - ' +
              moment(queryParams[key].value[1]).format('MMM D, YYYY'),
            queryParam: key,
            type: queryParams[key].type,
          });
          break;
        case 'status':
          const value = queryParams[key].name;
          if (!value) break; // Skip if no name resolved
          if (value instanceof Array) {
            value.forEach((subValue, index) => {
              if (subValue) {
                // Skip undefined/null names
                filterValues.push({
                  title: queryParams[key].title,
                  name: subValue,
                  queryParam: key,
                  type: queryParams[key].type,
                  valueIndex: index,
                });
              }
            });
          } else if (value) {
            filterValues.push({
              title: queryParams[key].title,
              name: value,
              queryParam: key,
              type: queryParams[key].type,
            });
          }
          break;
      }
    });

    return filterValues;
  }, [queryParams]);

  const removeFilter = filter => {
    const param = queryParams[filter.queryParam];
    const currentNames = param?.name;
    const currentValues = param?.value;

    if (
      currentValues instanceof Array &&
      currentValues.length > 1 &&
      filter.type !== 'date_range'
    ) {
      // Remove by index if available, otherwise by matching name
      let indexToRemove = filter.valueIndex;
      if (indexToRemove === undefined) {
        indexToRemove = currentNames?.findIndex(n => n === filter.name) ?? -1;
      }

      if (indexToRemove >= 0) {
        updateQueryParams({
          [filter.queryParam]: {
            ...param,
            name: currentNames?.filter((_, i) => i !== indexToRemove),
            value: currentValues.filter((_, i) => i !== indexToRemove),
            type: filter.type,
          },
        });
      }
    } else {
      updateQueryParams({ [filter.queryParam]: null });
    }
  };

  return (
    <div className={cn(styles.floatingFilters, 'surface-white', { [className]: className })}>
      <div className={styles.filterInputs}>
        <span className="u-margin-right--small">Filters</span> {children}
      </div>
      {filters.length > 0 && (
        <div
          className={cn(
            styles.filterValues,
            'surface-secondary u-padding--small u-border-radius--medium u-border',
          )}>
          <IconButton icon="x" type="delete" small onClick={clearQueryParams} />
          {filters.map(filter => {
            return (
              <div key={filter.name} className={cn(styles.filterPill, 'u-semibold')}>
                <span className={styles.filterPillTitle}>
                  {filter.type === 'date_range' && (
                    <FeatherIcon icon="calendar" size={16} className={styles.filterPillIcon} />
                  )}
                  {filter.title}
                </span>
                <span className={styles.filterPillValue}>
                  {filter.name}
                  <FeatherIcon
                    icon="x"
                    size={18}
                    className={styles.filterPillDelete}
                    onClick={() => removeFilter(filter)}
                  />
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
const FloatingFiltersLeft = ({ children }) => <div className={styles.left}>{children}</div>;
const FloatingFiltersRight = ({ children }) => <div className={styles.right}>{children}</div>;

const Filters = ({ children, className }) => (
  <div className={cn(styles.filters, { [className]: className })}>{children}</div>
);
const LeftFilters = ({ children }) => <div className={styles.left}>{children}</div>;
const RightFilters = ({ children }) => <div className={styles.right}>{children}</div>;

const Search = ({ placeholder = 'Search...', regex }) => {
  const { queryParams, updateQueryParams } = useTableContext();

  const [query, setQuery] = useState('');

  // Sync internal state with queryParams when search is cleared externally
  useEffect(() => {
    const searchValue = queryParams.search?.value ?? '';
    if (query !== searchValue) {
      setQuery(searchValue);
    }
  }, [queryParams.search?.value]);

  useEffect(() => {
    if (query === queryParams.search?.value) return;
    updateQueryParams({ search: { value: query }, page: { value: 1 } });
  }, [query]);

  return (
    <SearchInput
      placeholder={placeholder}
      className={styles.search}
      value={query}
      onChange={setQuery}
      regex={regex}
    />
  );
};

const DateRangeFilter = ({ title, filterName }) => {
  const { queryParams, updateQueryParams } = useTableContext();

  const [filterValue, setFilterValue] = useState(queryParams[filterName]?.value);

  // Update input from queryParams
  useEffect(() => {
    if (filterValue !== queryParams[filterName]?.value) {
      setFilterValue(queryParams[filterName]?.value);
    }
  }, [queryParams[filterName]]);

  // Update queryParams from input
  useEffect(() => {
    if (!filterValue) return;

    if (queryParams[filterName]?.value === filterValue) return;

    updateQueryParams({
      [filterName]: { title: title, name: title, value: filterValue, type: 'date_range' },
      page: { value: 1 },
    });
  }, [filterValue]);

  return (
    <div className={styles.filter}>
      <DateRangePicker
        value={filterValue}
        placeholder={title}
        onChange={setFilterValue}
        outputFormat="YYYY-MM-DD"
      />
    </div>
  );
};

const DateFilter = ({ title, filterName, locale = 'en_GB' }) => {
  const { dateHyphenFormat } = useLocale(locale);
  const { queryParams, updateQueryParams } = useTableContext();

  const [filterValue, setFilterValue] = useState(queryParams[filterName]?.value);

  useEffect(() => {
    if (!filterValue) return;

    if (queryParams[filterName] === filterValue) return;

    updateQueryParams({ [filterName]: { value: filterValue }, page: { value: 1 } });
  }, [filterValue]);

  return (
    <InputGroup title={title} className={cn(styles.filter, 'u-no-margin-bottom')}>
      <DatePicker onChange={setFilterValue} outputFormat="YYYY-MM-DD" format={dateHyphenFormat} />
    </InputGroup>
  );
};

const Filter = ({ title, filterName, options = [], useDefault = true, useSearch = false }) => {
  const { queryParams, updateQueryParams } = useTableContext();

  const [filterValue, setFilterValue] = useState(queryParams[filterName]?.value);

  useEffect(() => {
    if (!filterValue) return;

    if (queryParams[filterName]?.value === filterValue) return;

    updateQueryParams({
      [filterName]: {
        value: filterValue,
        name: (options ?? []).find(o => o.value === filterValue)?.name,
      },
      page: { value: 1 },
    });
  }, [filterValue, options?.length]);

  const opts = options ?? [];

  return (
    <Select
      className={styles.filter}
      inputProps={{ onChange: e => setFilterValue(e.target.value) }}
      value={filterValue}
      useDefault={useDefault}
      useSearch={useSearch}
      placeholder={title}>
      {opts.map(option => (
        <Select.Item key={option.value} value={option.value}>
          {option.name}
        </Select.Item>
      ))}
    </Select>
  );
};

const ensureArray = v => (Array.isArray(v) ? v : v != null ? [v] : []);

const MultiSelectFilter = ({
  title,
  filterName,
  type,
  options,
  disabled = false,
  useSearchInMultiSelect = false,
}) => {
  const { queryParams, updateQueryParams } = useTableContext();

  const [filterValue, setFilterValue] = useState(() => ensureArray(queryParams[filterName]?.value));

  // Update input from queryParams (re-run when options load so name→value fix can run)
  useEffect(() => {
    const param = queryParams[filterName];
    const paramValue = ensureArray(param?.value);
    if (JSON.stringify(filterValue) !== JSON.stringify(paramValue)) {
      setFilterValue(paramValue);
    }

    if (param?.value?.length !== param?.name?.length) {
      const names = param?.name;
      if (names && Array.isArray(names) && Array.isArray(options)) {
        const values = options.filter(o => names.includes(o.name)).map(o => o.value);
        setFilterValue(values);
      }
    }
  }, [queryParams[filterName], options?.length]);

  // Update queryParams from input (including empty selection so clearing all options applies correctly)
  useEffect(() => {
    const valueArr = ensureArray(filterValue);
    const paramValue = ensureArray(queryParams[filterName]?.value);
    if (JSON.stringify(valueArr) === JSON.stringify(paramValue)) return;

    const opts = options ?? [];
    updateQueryParams({
      [filterName]: {
        title: title,
        value: valueArr,
        name: valueArr.map(value => opts.find(o => o.value === value)?.name),
        type,
      },
      page: {
        value: 1,
      },
    });
  }, [filterValue, options?.length]);

  const valueArr = ensureArray(filterValue);

  return (
    <MultipleSelect
      className={styles.filter}
      value={valueArr}
      onChange={setFilterValue}
      placeholder={title}
      disabled={disabled}
      useSearch={useSearchInMultiSelect}
      hideLabel>
      {options?.map(option => (
        <MultipleSelect.Item
          key={option.value}
          value={option.value}
          active={valueArr.includes(option.value)}>
          {option.name}
        </MultipleSelect.Item>
      ))}
    </MultipleSelect>
  );
};

const BulkActions = ({ children }) => {
  const { selectedIds, bulkUseAll, setBulkUseAll } = useTableContext();

  return (
    <div className={cn(styles.bulkActions, { [styles.bulkActionsHidden]: selectedIds.length < 1 })}>
      <Badge className={styles.bulkActionsBadge} onClick={() => setBulkUseAll(!bulkUseAll)}>
        {bulkUseAll ? 'use all' : `${selectedIds.length} selected`}
      </Badge>
      <div className={styles.bulkActionsDivider} />
      <div className={styles.bulkActionsWrapper}>{children}</div>
    </div>
  );
};

const BulkAction = ({ icon, tip, onClick = () => {} }) => {
  const { selectedIds, bulkUseAll } = useTableContext();

  return (
    <OverlayTrigger placement="top" overlay={<Tooltip>{tip}</Tooltip>}>
      <div className={styles.bulkActionIconWrapper}>
        <IconComponent
          icon={icon}
          type="neutral"
          noBackground
          onClick={() => onClick(selectedIds, bulkUseAll)}
        />
      </div>
    </OverlayTrigger>
  );
};

Table.Context = Context;
Table.TableContent = TableContent;

Table.Head = Head;
Table.Header = Header;
Table.Body = Body;
Table.Row = Row;
Table.Col = Col;
Table.Pagination = Pagination;

Table.ActionButtons = ActionButtons;

Table.Filters = Filters;
Table.RightFilters = RightFilters;
Table.LeftFilters = LeftFilters;
Table.FloatingFilters = FloatingFilters;
Table.FloatingFiltersLeft = FloatingFiltersLeft;
Table.FloatingFiltersRight = FloatingFiltersRight;

Table.Search = Search;
Table.Filter = Filter;
Table.DateFilter = DateFilter;
Table.MultiSelectFilter = MultiSelectFilter;
Table.DateRangeFilter = DateRangeFilter;

Table.Placeholder = Placeholder;
Table.BulkActions = BulkActions;
Table.BulkAction = BulkAction;

export { useTableContext };
